import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Award, Users, Target, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { aboutData, timelineData, statsData, clientLogos } from '../data/mockData';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay }} className={className}>
      {children}
    </motion.div>
  );
};

const OverOnsPage = () => {
  return (
    <main className="pt-28 min-h-screen">
      <SEO title="Over Staffing Force – B Corp Detacheerder sinds 2007" description="Staffing Force is sinds 2007 duurzaam detacheringsbureau voor Finance, HR en Duurzaamheidsconsultants in Amsterdam. B Corp gecertificeerd, met focus op gelijke kansen op de werkvloer in heel Nederland." />

      <PageHero
        label="Wie wij zijn"
        title="Over Staffing Force"
        description={aboutData.description.slice(0, 180) + '...'}
        image="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Stats */}
      <section className="py-16 bg-[#00897B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {statsData.map((s, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">{s.number}</p>
                <p className="text-white/80 text-sm font-medium">{s.label}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Onze missie</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">Gelijke kansen op de werkvloer</h2>
                <p className="text-gray-600 leading-relaxed mb-6">{aboutData.mission}</p>
                <div className="bg-[#00897B]/5 border-l-4 border-[#00897B] p-4 rounded-r-lg">
                  <p className="text-[#00897B] font-medium text-sm">{aboutData.highlight}</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="grid grid-cols-2 gap-6">
                {[{ icon: Award, title: 'B Corp', desc: 'Eerste gecertificeerde bureau in NL' },
                  { icon: Users, title: '500+ Professionals', desc: 'Succesvol geplaatst' },
                  { icon: Target, title: 'Resultaatgericht', desc: 'Focus op kwaliteit' },
                  { icon: Heart, title: 'Duurzaam', desc: 'Maatschappelijk verantwoord' }].map((v, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <v.icon size={24} className="text-[#00897B] mb-3" />
                    <h3 className="font-bold text-gray-900 text-sm">{v.title}</h3>
                    <p className="text-gray-500 text-xs mt-1">{v.desc}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* About Description */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div className="rounded-2xl overflow-hidden shadow-2xl relative">
                <img src={aboutData.image} alt="Team Staffing Force" className="w-full h-[450px] object-cover" loading="lazy" />
                <div className="absolute -bottom-4 -right-4 bg-[#00897B] text-white p-5 rounded-xl shadow-xl">
                  <p className="text-2xl font-bold">Sinds 2007</p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div>
                <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Ons verhaal</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">Duurzaam detacheren</h2>
                <p className="text-gray-600 leading-relaxed mb-4">{aboutData.description}</p>
                <p className="text-gray-600 leading-relaxed">Binnen onze drie disciplines - Financieel, HR en Duurzaamheidsconsultants - werken wij van operationeel tot directieniveau binnen (internationale) organisaties.</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Ons verhaal</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3">Historie</h2>
            </div>
          </FadeIn>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#00897B]/20 hidden lg:block" />
            <div className="space-y-12 lg:space-y-0">
              {timelineData.map((item, index) => {
                const isLeft = index % 2 === 0;
                return (
                  <FadeIn key={item.year} delay={index * 0.1}>
                    <div className={`lg:flex items-center lg:mb-16 ${isLeft ? '' : 'lg:flex-row-reverse'}`}>
                      <div className={`lg:w-[calc(50%-2rem)] ${isLeft ? 'lg:pr-8 lg:text-right' : 'lg:pl-8'}`}>
                        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                          <span className="text-[#00897B] font-bold text-2xl">{item.year}</span>
                          <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2">{item.title}</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                      <div className="hidden lg:flex w-16 justify-center flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-[#00897B] border-4 border-white shadow-md" />
                      </div>
                      <div className="lg:w-[calc(50%-2rem)] hidden lg:block" />
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-20 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10">
          <FadeIn><div className="text-center">
            <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">Vertrouwd door</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-3">Onze klanten</h2>
          </div></FadeIn>
        </div>
        <div className="flex animate-marquee">
          {[...clientLogos, ...clientLogos, ...clientLogos].map((l, i) => (
            <div key={i} className="flex-shrink-0 mx-8 flex items-center justify-center">
              <img src={l.url} alt={l.name} className="h-16 w-auto object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-500" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0d1b2a]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Wilt u samenwerken?</h2>
            <p className="text-white/70 mb-8">Neem contact op en ontdek hoe Staffing Force u kan helpen.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/contact" className="bg-[#00897B] hover:bg-[#00796B] text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5">Neem contact op</Link>
              <Link to="/vacatures" className="border-2 border-white/30 hover:border-white text-white px-8 py-3 rounded font-semibold text-sm transition-all hover:-translate-y-0.5">Bekijk vacatures</Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default OverOnsPage;
